/**
 * Click Tracker Storage Utility for FE V3
 *
 * The storage key includes propertyCode (NOT propertyId) to prevent
 * cross-contamination between different hotels on the same centralized booking engine domain:
 * V3 localStorage: cb_amp_click_tracker_{propertyCode} (e.g., cb_amp_click_tracker_v0nsqg)
 *
 * Data is kept in localStorage even after expiry for debugging purposes,
 * but is only considered valid for attribution within 30 days of click.
 */
const STORAGE_KEY_PREFIX = 'cb_amp_click_tracker';
const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;
/**
 * Generate storage key for a specific property
 * @param propertyCode The property code from URL (e.g., "v0nsqg")
 */
function getStorageKey(propertyCode) {
    return `${STORAGE_KEY_PREFIX}_${propertyCode}`;
}
/**
 * Save click tracking data to localStorage
 * @param data Click tracking data to save
 * @param propertyCode The property code from URL (e.g., "v0nsqg") to associate this click with
 */
export function saveClickData(data, propertyCode) {
    try {
        const storageKey = getStorageKey(propertyCode);
        const trackerData = Object.assign(Object.assign({}, data), { timestamp: Date.now() });
        localStorage.setItem(storageKey, JSON.stringify(trackerData));
    }
    catch (error) {
        console.error('Failed to save click tracker data:', error);
    }
}
/**
 * Get click tracking data from localStorage (raw, even if expired)
 * @param propertyCode The property code from URL to get click data for
 */
export function getClickData(propertyCode) {
    try {
        const storageKey = getStorageKey(propertyCode);
        const raw = localStorage.getItem(storageKey);
        if (!raw) {
            return null;
        }
        const data = JSON.parse(raw);
        return data;
    }
    catch (error) {
        console.error('Failed to parse click tracker data:', error);
        return null;
    }
}
/**
 * Check if click data is valid (within 30 days)
 * This is the key validation used when attributing bookings to clicks
 */
export function isClickDataValid(data) {
    if (!data || !data.timestamp) {
        return false;
    }
    const ageMs = Date.now() - data.timestamp;
    return ageMs <= THIRTY_DAYS_MS;
}
/**
 * Get click data only if it's valid (within 30 days)
 * Use this when you need to attribute a booking to a click
 * @param propertyCode The property code from URL to get valid click data for
 */
export function getValidClickData(propertyCode) {
    const data = getClickData(propertyCode);
    return isClickDataValid(data) ? data : null;
}
/**
 * Clear click tracking data from localStorage
 * @param propertyCode The property code from URL to clear click data for
 */
export function clearClickData(propertyCode) {
    try {
        const storageKey = getStorageKey(propertyCode);
        localStorage.removeItem(storageKey);
    }
    catch (error) {
        console.error('Failed to clear click tracker data:', error);
    }
}
/**
 * Get the age of click data in days (for debugging/logging)
 */
export function getClickDataAgeDays(data) {
    if (!data || !data.timestamp) {
        return null;
    }
    const ageMs = Date.now() - data.timestamp;
    return Math.floor(ageMs / (24 * 60 * 60 * 1000));
}
/**
 * Format click data to cb_amp_click_id format (query string)
 *
 * This format is expected by the backend's concludeYourAmplifyClickIDJourney()
 */
export function formatClickDataForBackend(data) {
    const params = {
        [data.source]: data.clickId,
        bucket: data.bucket || '',
        country: data.country || '',
    };
    return new URLSearchParams(params).toString();
}
